import React, { useState } from 'react';
import { ArrowUpIcon, ArrowDownIcon, FileTextIcon, ShoppingCartIcon, ReceiptIcon, CreditCardIcon, DollarSignIcon, AlertCircleIcon, CheckCircleIcon, InfoIcon, XCircleIcon } from 'lucide-react';
import { LedgerEntry, DocumentType, DocumentStatus, LedgerState } from './LedgerView';
import { formatAmount } from '../utils/currencyFormatter';
interface LedgerTableProps {
  entries: LedgerEntry[];
  onSelectEntry: (entry: LedgerEntry) => void;
  locale: string;
}
export function LedgerTable({
  entries,
  onSelectEntry,
  locale
}: LedgerTableProps) {
  const [sortField, setSortField] = useState<keyof LedgerEntry>('createdDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  // Handle sort
  const handleSort = (field: keyof LedgerEntry) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };
  // Sort entries
  const sortedEntries = [...entries].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];
    // Handle null values
    if (aValue === null || aValue === undefined) aValue = 0;
    if (bValue === null || bValue === undefined) bValue = 0;
    // String comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortDirection === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    // Number comparison
    return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
  });
  // Get document type icon
  const getDocumentTypeIcon = (type: DocumentType) => {
    switch (type) {
      case 'Invoice':
        return <ReceiptIcon className="h-4 w-4 text-purple-500" data-id="element-10455" />;
      case 'Purchase Order':
        return <ShoppingCartIcon className="h-4 w-4 text-blue-500" data-id="element-10456" />;
      case 'Credit Note':
        return <FileTextIcon className="h-4 w-4 text-orange-500" data-id="element-10457" />;
      case 'Expense Claim':
        return <CreditCardIcon className="h-4 w-4 text-green-500" data-id="element-10458" />;
      case 'Contract':
        return <FileTextIcon className="h-4 w-4 text-indigo-500" data-id="element-10459" />;
      case 'Payment':
        return <DollarSignIcon className="h-4 w-4 text-emerald-500" data-id="element-10460" />;
      default:
        return <FileTextIcon className="h-4 w-4 text-gray-500" data-id="element-10461" />;
    }
  };
  // Get document status badge
  const getDocumentStatusBadge = (status: DocumentStatus) => {
    switch (status) {
      case 'Draft':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700" data-id="element-10462">
            Draft
          </span>;
      case 'Approved':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10463">
            Approved
          </span>;
      case 'Exported':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-10464">
            Exported
          </span>;
      case 'Paid':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-100 text-emerald-800" data-id="element-10465">
            Paid
          </span>;
      case 'Cancelled':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800" data-id="element-10466">
            Cancelled
          </span>;
      case 'Reversed':
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-800" data-id="element-10467">
            Reversed
          </span>;
      default:
        return <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10468">
            {status}
          </span>;
    }
  };
  // Get ledger state indicator
  const getLedgerStateIndicator = (state: LedgerState) => {
    switch (state) {
      case 'Informational':
        return <InfoIcon className="h-4 w-4 text-blue-400" title="Informational - Draft entry" data-id="element-10469" />;
      case 'Active':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" title="Active" data-id="element-10470" />;
      case 'Reversed':
        return <XCircleIcon className="h-4 w-4 text-red-500" title="Reversed" data-id="element-10471" />;
      default:
        return null;
    }
  };
  // Column header with sort capability
  const SortableHeader = ({
    field,
    label
  }: {
    field: keyof LedgerEntry;
    label: string;
  }) => <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-50" onClick={() => handleSort(field)} data-id="element-10472">
      <div className="flex items-center space-x-1" data-id="element-10473">
        <span data-id="element-10474">{label}</span>
        {sortField === field ? sortDirection === 'asc' ? <ArrowUpIcon className="h-3 w-3" data-id="element-10475" /> : <ArrowDownIcon className="h-3 w-3" data-id="element-10476" /> : null}
      </div>
    </th>;
  return <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-10477">
      <div className="overflow-x-auto" data-id="element-10478">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-10479">
          <thead className="bg-gray-50 sticky top-0" data-id="element-10480">
            <tr data-id="element-10481">
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10482">
                State
              </th>
              <SortableHeader field="createdDate" label="Date" data-id="element-10483" />
              <SortableHeader field="documentNumber" label="Document #" data-id="element-10484" />
              <SortableHeader field="documentType" label="Type" data-id="element-10485" />
              <SortableHeader field="orgUnitName" label="Org Unit" data-id="element-10486" />
              <SortableHeader field="glCode" label="GL Code" data-id="element-10487" />
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10488">
                Debit (DR)
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10489">
                Credit (CR)
              </th>
              <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10490">
                Balance
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-10491">
            {sortedEntries.length === 0 ? <tr data-id="element-10492">
                <td colSpan={9} className="px-6 py-8 text-center text-gray-500" data-id="element-10493">
                  <AlertCircleIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" data-id="element-10494" />
                  <p data-id="element-10495">No ledger entries found matching your criteria.</p>
                </td>
              </tr> : sortedEntries.map(entry => {
            const isInformational = entry.ledgerState === 'Informational';
            const isReversed = entry.ledgerState === 'Reversed';
            const isReversal = entry.ledgerEntryType === 'Reversal';
            return <tr key={entry.ledgerEntryId} className={`hover:bg-gray-50 cursor-pointer transition-colors ${isInformational ? 'bg-blue-50 bg-opacity-30' : ''} ${isReversed ? 'bg-red-50 bg-opacity-20' : ''}`} onClick={() => onSelectEntry(entry)} data-id="element-10496">
                    <td className="px-4 py-3 whitespace-nowrap" data-id="element-10497">
                      {getLedgerStateIndicator(entry.ledgerState)}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900" data-id="element-10498">
                      <div data-id="element-10499">
                        {new Date(entry.createdDate).toLocaleDateString(locale, {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-10500">
                        {new Date(entry.createdDate).toLocaleTimeString(locale, {
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap" data-id="element-10501">
                      <div className="text-sm font-medium text-gray-900" data-id="element-10502">
                        {entry.documentNumber}
                      </div>
                      {isReversal && entry.reversesLedgerEntryId && <div className="text-xs text-orange-600 flex items-center" data-id="element-10503">
                          <AlertCircleIcon className="h-3 w-3 mr-1" data-id="element-10504" />
                          Reverses entry
                        </div>}
                      <div className="text-xs text-gray-500" data-id="element-10505">
                        {getDocumentStatusBadge(entry.documentStatus)}
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500" data-id="element-10506">
                      <div className="flex items-center" data-id="element-10507">
                        {getDocumentTypeIcon(entry.documentType)}
                        <span className="ml-2" data-id="element-10508">{entry.documentType}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900" data-id="element-10509">
                      <div className="max-w-xs truncate" title={entry.orgUnitPath} data-id="element-10510">
                        {entry.orgUnitName}
                      </div>
                      {(entry.vendorName || entry.userName) && <div className="text-xs text-gray-500 truncate" data-id="element-10511">
                          {entry.vendorName || entry.userName}
                        </div>}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 font-mono" data-id="element-10512">
                      {entry.glCode}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-mono ${isInformational ? 'text-gray-400' : 'text-green-700 font-semibold'}`} data-id="element-10513">
                      {entry.debitAmount ? formatAmount(entry.debitAmount, locale) : ''}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-mono ${isInformational ? 'text-gray-400' : 'text-red-700 font-semibold'}`} data-id="element-10514">
                      {entry.creditAmount ? formatAmount(entry.creditAmount, locale) : ''}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap text-sm text-right font-mono font-bold ${isInformational ? 'text-gray-400' : 'text-indigo-700'}`} data-id="element-10515">
                      {entry.calculatedRunningBalance !== undefined ? formatAmount(entry.calculatedRunningBalance, locale) : ''}
                    </td>
                  </tr>;
          })}
          </tbody>
        </table>
      </div>

      {/* Legend */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-200" data-id="element-10516">
        <div className="flex items-center justify-between text-xs text-gray-600" data-id="element-10517">
          <div className="flex items-center space-x-4" data-id="element-10518">
            <div className="flex items-center" data-id="element-10519">
              <InfoIcon className="h-4 w-4 text-blue-400 mr-1" data-id="element-10520" />
              <span data-id="element-10521">Informational (Draft)</span>
            </div>
            <div className="flex items-center" data-id="element-10522">
              <CheckCircleIcon className="h-4 w-4 text-green-500 mr-1" data-id="element-10523" />
              <span data-id="element-10524">Active</span>
            </div>
            <div className="flex items-center" data-id="element-10525">
              <XCircleIcon className="h-4 w-4 text-red-500 mr-1" data-id="element-10526" />
              <span data-id="element-10527">Reversed</span>
            </div>
          </div>
          <div className="text-gray-500" data-id="element-10528">
            {sortedEntries.length}{' '}
            {sortedEntries.length === 1 ? 'entry' : 'entries'}
          </div>
        </div>
      </div>
    </div>;
}